import React, { useEffect, useState, createContext, useContext } from 'react';

// Import translation files
import enLanding from '../locales/en/landing.json';
import esLanding from '../locales/es/landing.json';

const TranslationContext = createContext();

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within TranslationProvider');
  }
  return context;
};

export const TranslationProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem('lang') || 'en');
  const [translations, setTranslations] = useState({});

  // Load translation files based on language
  useEffect(() => {
    const loadTranslations = () => {
      // The JSON files already have the structure with "landing" key
      // So we use them directly
      if (lang === 'es') {
        setTranslations(esLanding);
      } else {
        setTranslations(enLanding);
      }
    };

    loadTranslations();
  }, [lang]);

  // Translation function - supports nested keys like "landing.hero.title"
  const t = (key, params = {}) => {
    if (!key) return '';
    
    const keys = key.split('.');
    let value = translations;
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        // Fallback to key if translation not found
        console.warn(`Translation key not found: ${key}`);
        return key;
      }
    }
    
    // Replace parameters in the translation string
    if (typeof value === 'string' && Object.keys(params).length > 0) {
      return value.replace(/\{(\w+)\}/g, (match, paramKey) => {
        return params[paramKey] !== undefined ? params[paramKey] : match;
      });
    }
    
    return value || key;
  };

  // Change language
  const changeLanguage = (newLang) => {
    if (newLang === lang) return;
    
    localStorage.setItem('lang', newLang);
    setLang(newLang);
  };

  const value = {
    lang,
    t,
    changeLanguage,
    setLanguage: changeLanguage, // Alias for compatibility
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};
